<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import AiAssistSettingsPopover from './AiAssistSettingsPopover.vue';
import { AI_ASSIST_REQUEST_DRAFT } from '../events';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const popoverVisible = ref(false);
let hoverTimer = null;

const prefs = computed(() => store.getters.aiAssistPrefsFor(props.conversationId));
const busy = computed(() => store.getters.aiAssistBusyFor(props.conversationId));

const ensureLoaded = () => {
  store.dispatch('loadAiAssistPrefs', props.conversationId);
};

onMounted(ensureLoaded);
watch(() => props.conversationId, ensureLoaded);

const onTrigger = () => {
  if (busy.value) return;
  emitter.emit(AI_ASSIST_REQUEST_DRAFT, { conversationId: props.conversationId });
};

const handleEnter = () => {
  clearTimeout(hoverTimer);
  popoverVisible.value = true;
};

const handleLeave = () => {
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => {
    popoverVisible.value = false;
  }, 200);
};
</script>

<template>
  <div
    class="relative inline-flex items-center"
    @mouseenter="handleEnter"
    @mouseleave="handleLeave"
  >
    <button
      type="button"
      class="px-2 py-1 text-xs font-medium rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      :class="
        prefs.autoReplyOn
          ? 'bg-n-brand text-white border-transparent'
          : 'bg-n-alpha-1 text-n-slate-11 border-n-weak hover:bg-n-alpha-2'
      "
      :title="busy ? t('CUSTOM.AI_ASSIST.BUSY_TITLE') : t('CUSTOM.AI_ASSIST.TRIGGER_TITLE')"
      :disabled="busy"
      @click="onTrigger"
    >
      <span class="inline-flex items-center gap-1">
        <span class="text-[10px] uppercase tracking-wide">AI</span>
        <span
          v-if="busy"
          class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin"
        />
        <span>{{ busy ? t('CUSTOM.AI_ASSIST.PROCESSING') : t('CUSTOM.AI_ASSIST.ASSISTANT') }}</span>
      </span>
    </button>
    <AiAssistSettingsPopover
      v-if="popoverVisible"
      :conversation-id="conversationId"
      class="absolute right-0 top-full mt-2 z-40"
      @stay="handleEnter"
      @leave="handleLeave"
    />
  </div>
</template>
